import imageCompression from 'browser-image-compression';
import api from './api';

/**
 * Compress an image in the browser, then upload it to R2 via a backend-signed PUT URL.
 *
 * Flow:
 *   1) Compress to ~500 KB / 1920 px max via browser-image-compression
 *   2) Ask backend for a presigned PUT URL (`/api/uploads/sign` or `/sign-admin`)
 *   3) PUT the bytes directly to R2 (backend never touches them)
 *   4) Return the final CDN URL the form should store
 *
 * @param {File} file - raw image from <input type="file">
 * @param {"property"|"submission"|"service-request"} kind
 * @returns {Promise<string>} CDN URL
 */
export async function uploadImage(file, kind) {
  if (!file.type.startsWith('image/')) {
    throw new Error(`${file.name}: not an image`);
  }

  // 1) Client-side compress
  const compressed = await imageCompression(file, {
    maxSizeMB: 0.5,
    maxWidthOrHeight: 1920,
    useWebWorker: true,
    initialQuality: 0.82,
  });

  // 2) Ask backend to sign
  const endpoint = kind === 'property' ? '/uploads/sign-admin' : '/uploads/sign';
  const safeName = compressed.name.replace(/[^A-Za-z0-9._-]/g, '_');
  const { data } = await api.post(endpoint, {
    filename: safeName,
    contentType: compressed.type,
    kind,
  });

  // 3) PUT directly to R2 (use plain fetch — bypass our axios envelope interceptor)
  const putRes = await fetch(data.putUrl, {
    method: 'PUT',
    headers: { 'Content-Type': compressed.type },
    body: compressed,
  });
  if (!putRes.ok) {
    throw new Error(`Upload failed (${putRes.status}): ${file.name}`);
  }

  // 4) Return the CDN URL the backend gave us
  return data.cdnUrl;
}

/**
 * Upload many files in parallel. Returns one entry per input file in the same order,
 * each with { url, error }. Successful uploads have a string url; failures have an error.
 */
export async function uploadImages(files, kind) {
  const results = await Promise.allSettled(
    Array.from(files).map((file) => uploadImage(file, kind)),
  );
  return results.map((r, i) =>
    r.status === 'fulfilled'
      ? { url: r.value, name: files[i].name, error: null }
      : { url: null, name: files[i].name, error: r.reason?.message ?? 'Upload failed' },
  );
}
